import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Ban, CheckCircle2, Wallet, Trash2, Eye } from "lucide-react";
import api, { inr, fmtDate, fmtErr } from "../../lib/api";
import { AdminWalletAdjust } from "../../components/AdminWalletAdjust";
import { toast } from "sonner";

export default function AdminCustomers() {
  const [customers, setCustomers] = useState(null);
  const [adjusting, setAdjusting] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => api.get("/admin/customers").then(({ data }) => setCustomers(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (c, status) => {
    try {
      await api.put(`/admin/customers/${c.user_id}/status`, { status });
      toast.success(`${c.email} → ${status}`);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const loginAs = async (c) => {
    try {
      const { data } = await api.post(`/admin/customers/${c.user_id}/impersonate`);
      window.open(data.url, "_blank", "noopener");
      toast.success(`Opening ${c.email}'s panel — 15-min admin view; your admin session resumes automatically`);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const deleteCustomer = async (c) => {
    if (!window.confirm(`Permanently delete ${c.email}? Their warehouses, wallet and login access will be removed. This cannot be undone.`)) return;
    try {
      const { data } = await api.delete(`/admin/customers/${c.user_id}`);
      toast.success(`${c.email} deleted — removed: ${Object.entries(data.deleted || {}).map(([k, v]) => `${k} (${v})`).join(", ") || "account"}`);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div data-testid="admin-customers-page">
      <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">Customers</h1>
      <p className="text-sm text-slate-500">{customers?.length ?? 0} registered shippers.</p>

      <div className="mt-6 overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="customers-table">
        <table className="w-full min-w-[820px] text-sm">
          <thead>
            <tr className="bg-shipany-navy text-left text-xs uppercase tracking-wider text-white">
              <th className="px-5 py-3.5">Customer</th><th className="px-4 py-3.5">Company</th><th className="px-4 py-3.5">Auth</th>
              <th className="px-4 py-3.5">Wallet</th><th className="px-4 py-3.5">Shipments</th><th className="px-4 py-3.5">Joined</th><th className="px-4 py-3.5">Status</th><th className="px-4 py-3.5"></th>
            </tr>
          </thead>
          <tbody>
            {(customers || []).map((c) => (
              <tr key={c.user_id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`customer-row-${c.email}`}>
                <td className="px-5 py-3.5"><p className="cursor-pointer font-semibold text-shipany-navy hover:text-shipany-blue hover:underline" onClick={() => navigate(`/admin/customers/${c.user_id}`)}>{c.name}</p><p className="text-xs text-slate-400">{c.email}</p></td>
                <td className="px-4 py-3.5 text-slate-500">{c.company || "—"}</td>
                <td className="px-4 py-3.5"><span className="rounded bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-500">{c.auth_provider || "jwt"}</span></td>
                <td className="mono px-4 py-3.5 font-semibold">{inr(c.wallet_balance)}</td>
                <td className="mono px-4 py-3.5">{c.shipment_count}</td>
                <td className="px-4 py-3.5 text-slate-500">{fmtDate(c.created_at)}</td>
                <td className="px-4 py-3.5">
                  {c.status === "active"
                    ? <button onClick={() => setStatus(c, "suspended")} data-testid={`suspend-${c.email}`} className="flex items-center gap-1.5 rounded-lg bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-600 hover:bg-red-50 hover:text-red-600"><CheckCircle2 className="h-3.5 w-3.5" /> Active</button>
                    : <button onClick={() => setStatus(c, "active")} data-testid={`activate-${c.email}`} className="flex items-center gap-1.5 rounded-lg bg-red-50 px-3 py-1.5 text-xs font-bold text-red-600 hover:bg-emerald-50 hover:text-emerald-600"><Ban className="h-3.5 w-3.5" /> Suspended</button>}
                </td>
                <td className="px-4 py-3.5 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => navigate(`/admin/customers/${c.user_id}`)} data-testid={`view-${c.email}`}
                      className="flex items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-200">
                      <Eye className="h-3.5 w-3.5" /> View
                    </button>
                    <button onClick={() => loginAs(c)} data-testid={`login-as-${c.email}`}
                      className="flex items-center gap-1.5 rounded-lg bg-indigo-50 px-3 py-1.5 text-xs font-bold text-indigo-600 hover:bg-indigo-100">
                      Login as
                    </button>
                    <button onClick={() => setAdjusting(c)} data-testid={`add-balance-${c.email}`}
                      className="flex items-center gap-1.5 rounded-lg bg-shipany-blue px-3 py-1.5 text-xs font-bold text-white hover:bg-[#0648B3]">
                      <Wallet className="h-3.5 w-3.5" /> Add Balance
                    </button>
                    <button onClick={() => deleteCustomer(c)} data-testid={`delete-${c.email}`}
                      className="flex items-center gap-1.5 rounded-lg bg-red-50 px-3 py-1.5 text-xs font-bold text-red-600 hover:bg-red-100">
                      <Trash2 className="h-3.5 w-3.5" /> Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {adjusting && <AdminWalletAdjust target={{ user_id: adjusting.user_id, email: adjusting.email, name: adjusting.name, balance: adjusting.wallet_balance }} onClose={() => setAdjusting(null)} onDone={load} />}
    </div>
  );
}
